import React from "react";
import { Alert } from "@mui/material";
import IconCranialAnalysis from "@mui/icons-material/StraightenOutlined";
import AreaTitle from "../../../components/areaTitle/AreaTitle";
import useCranialAnalysis from "./hooks/useCranialAnalysis";
import { isCameraSupported } from "./hooks/useCameraCapture";
import ImageSourceSelector from "./components/ImageSourceSelector";
import GuidedCamera from "./components/GuidedCamera";
import ImagePreview from "./components/ImagePreview";
import ImageValidationStep from "./components/ImageValidationStep";
import AiAnalysisStep from "./components/AiAnalysisStep";
import CranialEditor from "./components/editor/CranialEditor";
import CranialResultsPanel from "./components/CranialResultsPanel";
import styles from "./CranialAnalysisPage.module.css";

const STEP_GROUPS = [
  { label: "Foto", steps: ["source", "camera", "preview"] },
  { label: "Verificação", steps: ["validation"] },
  { label: "Análise", steps: ["analysis"] },
  { label: "Ajuste", steps: ["editor"] },
  { label: "Resultado", steps: ["result"] },
];

const EXPERIMENTAL_TEXT =
  "Ferramenta experimental de medição assistida. Os valores dependem de perspectiva, distância e posicionamento e não substituem avaliação profissional.";

function StepIndicator({ step }) {
  const activeIndex = STEP_GROUPS.findIndex((group) => group.steps.indexOf(step) !== -1);

  return (
    <ol className={styles.stepper} aria-label="Etapas da análise">
      {STEP_GROUPS.map((group, index) => {
        const state = index < activeIndex ? styles.stepDone : index === activeIndex ? styles.stepActive : "";
        return (
          <li key={group.label} className={`${styles.step} ${state}`} aria-current={index === activeIndex ? "step" : undefined}>
            <span className={styles.stepNumber}>{index + 1}</span>
            <span className={styles.stepLabel}>{group.label}</span>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Análise craniana por foto (experimental).
 *
 * Orchestrates the flow source -> camera -> preview -> validation -> analysis ->
 * editor -> result. All state lives in useCranialAnalysis and is discarded on
 * leave or reset; nothing is linked to patients or persisted.
 */
function CranialAnalysisPage() {
  const { state, measurements, actions } = useCranialAnalysis();
  const cameraAvailable = isCameraSupported();
  const { step, image, error } = state;

  const renderStep = () => {
    switch (step) {
      case "camera":
        return (
          <GuidedCamera
            onCapture={(blob) => actions.setCapturedFile(blob, "camera")}
            onCancel={actions.reset}
            onUseUpload={() => actions.chooseSource("upload")}
          />
        );
      case "preview":
        return (
          <ImagePreview
            image={image}
            sourceType={state.sourceType}
            isPreparing={state.isPreparing}
            onRotate={actions.rotateImage}
            onUsePhoto={actions.usePhoto}
            onRetake={() => actions.retake()}
          />
        );
      case "validation":
        return (
          <ImageValidationStep
            validation={state.validation}
            cameraAvailable={cameraAvailable}
            onValidate={actions.validate}
            onContinue={actions.analyze}
            onRetakeCamera={() => actions.retake("camera")}
            onChooseAnother={() => actions.retake("source")}
          />
        );
      case "analysis":
        return (
          <AiAnalysisStep
            isAnalyzing={state.isAnalyzing}
            aiSuggestion={state.aiSuggestion}
            error={error}
            sourceType={state.sourceType}
            onRetry={actions.retryAnalysis}
            onManual={actions.startManualTracing}
            onContinue={actions.openEditor}
            onRetake={() => actions.retake()}
          />
        );
      case "editor":
        return (
          <CranialEditor
            imageUrl={image.display.url}
            imageWidth={image.display.width}
            imageHeight={image.display.height}
            geometry={state.geometry}
            aiSuggestion={state.aiSuggestion}
            calibration={state.calibration}
            measurements={measurements}
            onGeometryChange={actions.updateGeometry}
            onCalibrationChange={actions.updateCalibration}
            onRestoreSuggestion={actions.restoreSuggestion}
            onConfirm={actions.confirmMeasurement}
          />
        );
      case "result":
        return (
          <CranialResultsPanel
            image={image}
            geometry={state.geometry}
            calibration={state.calibration}
            measurements={measurements}
            aiSuggestion={state.aiSuggestion}
            validation={state.validation}
            onBackToEditor={actions.backToEditor}
            onReset={actions.reset}
          />
        );
      case "source":
      default:
        return (
          <ImageSourceSelector
            cameraAvailable={cameraAvailable}
            isPreparing={state.isPreparing}
            onCamera={() => actions.chooseSource("camera")}
            onUpload={(file) => actions.setCapturedFile(file, "upload")}
          />
        );
    }
  };

  return (
    <div className="page-content">
      <div className={styles.pageHeader}>
        <AreaTitle
          Title="Análise craniana por foto"
          Icon={<IconCranialAnalysis />}
          Description="Ferramenta experimental de apoio à medição: a partir de uma foto da cabeça vista de cima, sugere o contorno e calcula comprimento, largura, diagonais, CVA, CVAI e índice cefálico. Não faz diagnóstico e nada é salvo."
        />
      </div>

      <StepIndicator step={step} />

      {error && step !== "analysis" && (
        <Alert severity="warning" onClose={actions.clearError} className={styles.errorBanner}>
          {error.message}
        </Alert>
      )}

      <div className={styles.stepContent}>{renderStep()}</div>

      <p className={styles.footer}>{EXPERIMENTAL_TEXT}</p>
    </div>
  );
}

export default CranialAnalysisPage;
